// src/components/game/AutarkyGameEngine.jsx
import React, { useReducer, useEffect, useRef } from 'react';
import StartScreen from './StartScreen';
import { FractureStage } from './stages/FractureStage';
import { AscentStage } from './stages/AscentStage';
import { RecalibrationStage } from './stages/RecalibrationStage';
import HUD from './ui/HUD';
import { ASSETS } from '../../utils/GameAssets';

const initialState = {
  age: 'Intro', 
  profession: 'Logistics', 
  resources: 500,
  integrity: 50,
  targetCountry: 'Canada', 
  tools: [],
  finalResult: null
};

function gameReducer(state, action) {
  switch (action.type) {
    case 'SET_PROFILE':
      return { ...state, ...action.payload, age: 'Fracture' };
    case 'ADVANCE_AGE':
      return { ...state, age: action.payload };
    case 'UPDATE_STATS':
      return { 
        ...state, 
        resources: state.resources + (action.payload.resources || 0),
        integrity: state.integrity + (action.payload.integrity || 0),
        tools: action.payload.tool ? [...state.tools, action.payload.tool] : state.tools
      };
    case 'GAME_OVER':
      return { ...state, age: 'GameOver', finalResult: action.payload };
    default:
      return state;
  }
}

export default function AutarkyGameEngine() {
  const [gameState, dispatch] = useReducer(gameReducer, initialState);
  const audioRef = useRef(new Audio());

  useEffect(() => {
    let themeKey = '';
    if (gameState.age === 'Fracture') themeKey = 'Fracture';
    else if (gameState.age === 'Ascent') themeKey = 'Ascent';
    else if (gameState.age === 'Recalibration') themeKey = 'Recalibration';

    if (themeKey) {
        const themeSrc = ASSETS.THEMES[themeKey];
        if (themeSrc && audioRef.current.src !== themeSrc) {
            audioRef.current.src = themeSrc;
            audioRef.current.loop = true;
            audioRef.current.volume = 0.3;
            audioRef.current.play().catch(() => {});
        }
    } else {
        audioRef.current.pause();
    }
  }, [gameState.age]);

  // SCALED BADGE LOGIC
  const getBadge = () => {
      const i = gameState.integrity;
      if (i > 200) return ASSETS.BADGES.Master; // Raised threshold
      if (i > 80) return ASSETS.BADGES.Integrity;
      return ASSETS.BADGES.Initiate;
  };

  return (
    <div className="h-screen w-full bg-gray-900 text-white overflow-hidden flex flex-col relative">
      {gameState.age !== 'Intro' && gameState.age !== 'GameOver' && <HUD stats={gameState} />}

      <div className="game-viewport flex-grow relative z-10 overflow-y-auto">
        
        {gameState.age === 'Intro' && <StartScreen dispatch={dispatch} />}
        
        {gameState.age === 'Fracture' && (
          <FractureStage dispatch={dispatch} profession={gameState.profession} resources={gameState.resources} tools={gameState.tools} integrity={gameState.integrity} />
        )}
        
        {gameState.age === 'Ascent' && (
          <AscentStage dispatch={dispatch} profession={gameState.profession} integrity={gameState.integrity} resources={gameState.resources} country={gameState.targetCountry} />
        )}
        
        {gameState.age === 'Recalibration' && (
          <RecalibrationStage dispatch={dispatch} resources={gameState.resources} tools={gameState.tools} integrity={gameState.integrity} />
        )}

        {/* GAME OVER WITH AGE 3 IMAGE */}
        {gameState.age === 'GameOver' && gameState.finalResult && (
            <div className="absolute inset-0 bg-slate-900 z-50 flex items-center justify-center p-6">
                <div className="w-full max-w-5xl bg-black border-2 border-yellow-600 rounded-lg p-0 shadow-2xl relative overflow-hidden flex flex-col md:flex-row h-[500px]">
                    
                    {/* Background: The Age 3 Outcome Image */}
                    <div className="absolute inset-0 z-0">
                        <img 
                            src={gameState.finalResult.img || ASSETS.STILLS.StartScreen} 
                            alt="Ending" 
                            className="w-full h-full object-cover opacity-40" 
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
                    </div>
                    
                    {/* Left: Badge & Status */}
                    <div className="flex-1 flex flex-col items-center justify-center z-10 border-r border-gray-800 p-8">
                        <img src={getBadge()} alt="Rank Badge" className="w-48 h-48 object-contain mb-6 drop-shadow-[0_0_15px_rgba(234,179,8,0.5)]" />
                        <h2 className="text-4xl font-bold text-yellow-500 uppercase tracking-widest text-center">{gameState.finalResult.status}</h2>
                    </div>

                    {/* Right: The File */}
                    <div className="flex-[1.5] z-10 flex flex-col justify-center p-10">
                        <div>
                            <h1 className="text-5xl font-bold text-white mb-2">{gameState.finalResult.government}</h1>
                            <p className="text-gray-300 text-lg mb-8 font-serif italic border-l-4 border-yellow-500 pl-4">
                                "{gameState.finalResult.desc}"
                            </p>
                            
                            <div className="grid grid-cols-3 gap-4 mb-8">
                                <div className="bg-black/50 p-4 rounded border border-gray-700 text-center">
                                    <div className="text-[10px] text-gray-500 uppercase">Economy</div>
                                    <div className="text-2xl font-bold text-green-400">{gameState.finalResult.scores.economy}</div>
                                </div>
                                <div className="bg-black/50 p-4 rounded border border-gray-700 text-center">
                                    <div className="text-[10px] text-gray-500 uppercase">Justice</div>
                                    <div className="text-2xl font-bold text-blue-400">{gameState.finalResult.scores.justice}</div>
                                </div>
                                <div className="bg-black/50 p-4 rounded border border-gray-700 text-center">
                                    <div className="text-[10px] text-gray-500 uppercase">Environment</div>
                                    <div className="text-2xl font-bold text-yellow-400">{gameState.finalResult.scores.environment}</div>
                                </div>
                            </div>
                        </div>

                        <button onClick={() => window.location.reload()} className="w-full py-4 bg-red-800 hover:bg-red-700 text-white font-bold rounded uppercase tracking-widest shadow-lg transition-all">
                            Archive & Restart
                        </button>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
}